import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import PricingChart from "../../../components/PricingChart.tsx";
import {remaining_period} from "../../../util/RemainingPeriod.tsx";
import type {UserInfoData} from "../../../lib/ModelTypes.ts";
import {frontEndType} from "../../../build_constants.ts";
import UserInfoVoramall from "./UserInfoVoramall.tsx";
import {Navbar} from "../../../components/Navbar.tsx";
import AuthContext from "../../../components/AuthContext.ts";
import Extension from "./Extension.tsx";
import {extension, ExtensionAPIConnectorError} from "../../../lib/Extension.ts";
import {BlueshiftAPIConnectorError} from "../../../lib/api.ts";
import {et} from "../../../locales/i18n.tsx";
import { useTranslation } from "react-i18next";

interface ContentProps {
    toggleSidebar: () => void;
}

const UserInfo = ({toggleSidebar}: ContentProps) => {
    const { t, i18n } = useTranslation("ui");

    const [data, setData] = useState<{
        userInfo: UserInfoData,
        remain: string,
        total_days: number,
        remain_days: number,
        extension_use_period: number,
    }>({
        userInfo: {
            id: "",
            email: "",
            signed: "",
            status: 0,
            expiration_date: "",
            use_start_date: "",
        },
        remain: "",
        total_days: 0,
        remain_days: 0,
        extension_use_period: 0,
    });
    const [isShowExtensionForm, setShowExtensionForm] = useState(false);

    const onSubmitExtension = (success: boolean) => {
        setShowExtensionForm(false);
        if (success) {
            setData((prev) => ({
                ...prev,
                extension_use_period: success ? 1 : 0
            }));
        }
    };
    const authContext = useContext(AuthContext);

    const init = useCallback(async () => {
        const result = authContext!.userInfo;

        setData((prev) => ({...prev, userInfo: result}))
        setData((prev) => ({...prev, remain: remaining_period(new Date(), new Date(result.expiration_date), t)}))
        setData((prev) => ({...prev, total_days: days(new Date(), new Date(result.expiration_date))}))
        setData((prev) => ({
            ...prev,
            remain_days: days(new Date(result.use_start_date), new Date(result.expiration_date))
        }));

        try {
            const res = await extension.extensionUsePeriod(authContext!.userInfo.id);
            setData((prev) => ({
                ...prev,
                extension_use_period: res
            }));
        } catch (err) {
            if (err instanceof BlueshiftAPIConnectorError) {
                throw new ExtensionAPIConnectorError(err, et(err.cause));
            }
            throw err;
        }
    }, [authContext, t]);


    useEffect(() => {
        init().finally();
    }, [init]);

    const remainText = useMemo(() => {
        return data.userInfo.expiration_date
            ? remaining_period(new Date(), new Date(data.userInfo.expiration_date), t)
            : "";
    }, [data.userInfo.expiration_date, t]);

    function days(now: Date, future: Date) {
        const diffTime = Math.floor((future.getTime() - now.getTime()) / (1000));
        return Math.floor(diffTime / (24 * 60 * 60));
    }

    const chartData = {
        labels: [],
        datasets: [
            {
                data: [data.remain_days, data.total_days - data.remain_days],
                backgroundColor: ["#0229bb", "#bcfbff"],
                borderColor: ["#0229bb", "#bcfbff"],
                borderWidth: 0
            },
        ],
    };

    return (
        <main className="d-block col ps-2 pt-3 min-vh-100">
            <Navbar toggleSidebar={toggleSidebar}/>
            <article className="blueshift_main" id="wrapper">
                <div className="bg-light py-2">
                    <h5 className="mb-0 text-primary">&emsp;{data.userInfo.id === "" ? t("userInfo.id") : data.userInfo.id}&emsp;
                        <div className="vr text-dark"
                             style={{width: "2px", verticalAlign: "middle"}}></div>
                        &emsp;<span
                            id="user_email">{data.userInfo.email === "" ? t("userInfo.email") : data.userInfo.email}</span>
                    </h5>
                </div>

                <div className="p-4">
                    <h6 className="text-primary col-6">{t("userInfo.myPlan")}</h6>
                </div>
                <div className="content">
                    {isShowExtensionForm ?
                        <Extension expirationDate={data.userInfo.expiration_date} userId={data.userInfo.id}
                                   onSubmit={onSubmitExtension}/> :
                        <>
                            <div className="align-items-center justify-content-center">
                                <div className="text-center">
                                    <h6 className="mb-4 text-secondary">  {t("userInfo.appliedAt")}&ensp;:&ensp;
                                        {data.userInfo.use_start_date !== ""
                                            ? new Date(data.userInfo.use_start_date).toLocaleString(i18n.language)
                                            : ""}
                                        <span className="text-primary"></span>
                                    </h6>
                                </div>
                                <div className="d-block">
                                    {data.remain !== "" ? <PricingChart key={i18n.language} chartData={chartData} remain={remainText}/> :
                                        <div></div>}
                                </div>

                                <h6 className="mt-4 text-secondary text-center">{data.userInfo.expiration_date !== ""
                                    ? new Date(data.userInfo.expiration_date).toLocaleString(i18n.language)
                                    : ""} {t("userInfo.expires")}</h6>
                            </div>
                            {
                                (data.total_days <= 5 && data.extension_use_period == 0)
                                    ? <UserInfoVoramall
                                        type={frontEndType}
                                        handleClick={() => setShowExtensionForm(true)}
                                    />
                                    : <></>
                            }
                        </>}
                </div>
            </article>
        </main>
    );
}

export default UserInfo;